// src/context/SuggestedContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../api';                // ✅ your one-and-only axios instance
import { FALLBACK_IMG } from '../utils/image';

const Ctx = createContext(null);

const SUG_CACHE_KEY = 'bf_suggested_v2';
const TOP_CACHE_KEY = 'bf_top10_v1';

function normalizeImages(p) {
  const imgs = Array.isArray(p.images) ? p.images : [];
  const first = imgs
    .map(x => (typeof x === 'string' ? x : x?.url))
    .find(u => u && !/placeholder\.com/i.test(u));
  return { ...p, images: [first || FALLBACK_IMG] };
}

export function SuggestedProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SUG_CACHE_KEY) || '[]'); } catch { return []; }
  });
  const [top10, setTop10] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TOP_CACHE_KEY) || '[]'); } catch { return []; }
  });
  const [error, setError] = useState(null);

  const inflight = useRef(false);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;

    try {
      setLoading(true);
      setError(null);

      // If there’s no JWT yet, don’t spam the API — just keep cache.
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        inflight.current = false;
        return;
      }

      const { data } = await api.get('/products/suggested?limit=20');
      const cleanedProducts = (data?.products || []).map(normalizeImages);
      const cleanedTop10 = (data?.top10 || []).map?.(normalizeImages) || []; // backend may not send top10

      setProducts(cleanedProducts);
      setTop10(cleanedTop10);

      localStorage.setItem(SUG_CACHE_KEY, JSON.stringify(cleanedProducts));
      localStorage.setItem(TOP_CACHE_KEY, JSON.stringify(cleanedTop10));
    } catch (e) {
      // Gracefully handle 401 (no token / expired) without noisy UI
      if (e?.response?.status === 401) {
        setError(null);
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to load suggestions');
      }
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = {
    loading,
    products,
    top10,
    error,
    refetch: load,
    updateAfterInteraction: async () => { await load(); },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSuggested = () => useContext(Ctx);
